import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../app/AuthProvider';
import { OtpError, sendOTP, verifyOTP, type VerifyOtpReason } from '../../lib/otp';
import { OTP_DEFAULT_DEV } from '../../lib/otp-core';
import { ACCOUNT_STATUS_QUERY_KEY, useAccountStatus } from '../../shared/hooks/useAccountStatus';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { PageLoader } from '../../shared/ui/PageLoader';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { AuthLayout } from './AuthLayout';
import { verifyOtpSchema, type VerifyOtpInput } from './schemas';
import s from './VerifyPhonePage.module.css';

type SendState =
  { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent' } | { kind: 'error'; message: string };

interface LocationState {
  from?: string;
}

/** Seconds before "Resend code" becomes available again. */
const RESEND_COOLDOWN = 30;

const REASON_MESSAGE: Record<VerifyOtpReason, string> = {
  // Not empty: the server can return {ok:false, reason:'ok'}, and the old empty
  // string combined with a `!== ''` guard rendered NOTHING for that case —
  // a submit that silently did nothing.
  ok: 'That did not go through. Send a new code and try again.',
  wrong_code: 'Wrong code. Try again.',
  expired: 'This code expired. Send a new one and try again.',
  too_many_attempts: 'Too many attempts. Send a new code and try again.',
  no_active_verification: 'No active code. Tap "Send a new code" to start.',
};

export function VerifyPhonePage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { session, user, loading: authLoading } = useAuth();
  const { status, loading: statusLoading } = useAccountStatus(user?.id ?? null);

  const [sendState, setSendState] = useState<SendState>({ kind: 'idle' });
  const [cooldown, setCooldown] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const autoSent = useRef(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<VerifyOtpInput>({
    resolver: zodResolver(verifyOtpSchema),
    defaultValues: { code: '' },
  });

  const handleSend = useCallback(async (): Promise<void> => {
    setSendState({ kind: 'sending' });
    setSubmitError(null);
    try {
      await sendOTP();
      setSendState({ kind: 'sent' });
      setRemaining(null);
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      const message = err instanceof OtpError ? err.message : 'Could not send the code. Try again.';
      setSendState({ kind: 'error', message });
    }
  }, []);

  // Send on arrival. The old screen required pressing "Send code" first and
  // kept the code box disabled until then — and because a reload reset that
  // state, a returning user was forced to request a new code, which
  // invalidated the one they already had (issue_otp expires the previous one).
  const canAutoSend = !authLoading && session !== null && status !== null && !status.phoneVerified;
  useEffect(() => {
    if (!canAutoSend || autoSent.current) return;
    autoSent.current = true;
    void handleSend();
  }, [canAutoSend, handleSend]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  if (authLoading || statusLoading) {
    return <PageLoader />;
  }

  if (session === null) {
    return <Navigate to="/login" replace state={{ from: '/verify-phone' }} />;
  }

  if (status !== null && status.phoneVerified) {
    return <Navigate to="/app" replace />;
  }

  async function onSubmit(values: VerifyOtpInput): Promise<void> {
    setSubmitError(null);
    try {
      const result = await verifyOTP(values.code);
      if (!result.ok) {
        // eslint-disable-next-line security/detect-object-injection -- result.reason is a typed union from the edge function
        setSubmitError(REASON_MESSAGE[result.reason]);
        setRemaining(result.remaining_attempts);
        reset({ code: '' });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_STATUS_QUERY_KEY });
      const state = location.state as LocationState | null;
      navigate(state?.from ?? '/app', { replace: true });
    } catch (err) {
      const message =
        err instanceof OtpError ? err.message : 'Could not verify the code. Try again.';
      setSubmitError(message);
    }
  }

  return (
    <AuthLayout
      pitchTitle={
        <>
          One quick check, then you&apos;re <em>in.</em>
        </>
      }
      pitchBody="We verify your phone once so tutors and institute admins know they're working with a real account."
    >
      <span className={s.eyebrow}>Last step</span>
      <h1 className={s.title}>Verify your phone</h1>
      <p className={s.subtitle}>
        Signed in as <strong>{session.user.email ?? 'your account'}</strong>.
      </p>

      <StatusBanner tone="info">
        Enter <code className={s.devCode}>{OTP_DEFAULT_DEV}</code> to continue — SMS verification is
        coming soon.
      </StatusBanner>

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.form}
      >
        <div>
          <Label htmlFor="code">Verification code</Label>
          <Input
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            maxLength={8}
            placeholder="0000"
            invalid={errors.code !== undefined}
            aria-describedby={errors.code ? 'code-error' : 'code-help'}
            className={s.codeInput}
            {...register('code')}
          />
          {errors.code !== undefined ? (
            <FormError id="code-error" message={errors.code.message} />
          ) : (
            <p id="code-help" className={s.smallHint}>
              {sendState.kind === 'sending'
                ? 'Sending a code…'
                : sendState.kind === 'sent'
                  ? 'A code was issued for your phone.'
                  : 'Enter the code issued for your phone.'}
            </p>
          )}
        </div>

        {sendState.kind === 'error' ? (
          <StatusBanner tone="error">{sendState.message}</StatusBanner>
        ) : null}

        {submitError !== null ? (
          <StatusBanner tone="error">
            {submitError}
            {remaining !== null && remaining > 0 ? (
              <>
                {' '}
                {remaining} {remaining === 1 ? 'attempt' : 'attempts'} left before you need a new
                code.
              </>
            ) : null}
          </StatusBanner>
        ) : null}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Verify and continue
        </Button>

        <div className={s.resendRow}>
          <Button
            variant="ghost"
            disabled={cooldown > 0 || sendState.kind === 'sending'}
            loading={sendState.kind === 'sending'}
            onClick={() => {
              void handleSend();
            }}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send a new code'}
          </Button>
        </div>
      </form>

      <p className={s.footer}>
        Wrong account? <Link to="/login">Switch</Link> or <Link to="/">go home</Link>.
      </p>
    </AuthLayout>
  );
}
